import { createAction } from '@reduxjs/toolkit'
import { ResponseData } from 'initAppAPI'
import size from 'lodash/size'
import { appListenerMiddleware } from 'reduxTyped'
import { l } from '@manychat/manyui'

import * as audienceActions from 'common/audience/actions/audienceActions'
import { alert } from 'common/core'
import { addNotification } from 'common/core/actions/uiActions'
import { isFetchPricingLimitInfo } from 'common/core/selectors/appSelectors'
import { WebSocketEvents } from 'constants/WebSockets'
import { settingsApi } from 'shared/api/requests/settings'
import {
  type ConversionsApiChannel,
  ConversionsApiValue,
  SettingsType,
  LiveChatAccountSettingsRequest,
  LiveChatAccountSettings,
} from 'shared/api/requests/settings/schemas'
import { createAsyncAction } from 'shared/lib/redux'
import { isRequestError } from 'utils/api/mcApi/RequestError'
import { isPathnameInclude } from 'utils/router/tools'
import {
  accountNotificationsListener,
  anotherTabNotificationsListener,
} from 'utils/services/notificationsService'

import { ConversionsApiStatus } from '../interfaces'

export const fetchSettings = createAsyncAction('settings/fetchSettings', async () => {
  const { data } = await settingsApi.fetchSettings()
  return data
})

export const updateAutomationPauseTime = createAsyncAction(
  'settings/updateAutomationPauseTime',
  async (pauseValue: number) => {
    await settingsApi.changeAutomationPauseInitTime({
      body: { second_interval: pauseValue },
    })

    alert(l.translate('Pause duration updated'), 'success')
  },
)

export const transferOwnership = createAsyncAction(
  'settings/transferOwnership',
  async (userId: number) => {
    await settingsApi.transferOwnership({ body: { user_id: userId } })
    alert(l.translate('Ownership has been transferred!'), 'success')
    window.location.reload()
  },
)

// Whitelisting
// TODO: remove when adds will be removed (getWhitelistingDomains, addWhitelistingDomain, deleteWhitelistingDomain)
export const getWhitelistingDomains = createAsyncAction(
  'settings/getWhitelistingDomains',
  async () => {
    const response = await settingsApi.growthTools.getWhitelistingDomains()
    return response.data
  },
)

export const addWhitelistingDomain = createAsyncAction(
  'settings/addWhitelistingDomain',
  async (url: string) => {
    const response = await settingsApi.growthTools.addWhitelistingDomain({ body: { url } })
    return response.data
  },
  {
    autoHandleByGuards: [settingsApi.growthTools.addWhitelistingDomain.isInvalidUrlError],
  },
)

export const deleteWhitelistingDomain = createAsyncAction(
  'settings/deleteWhitelistingDomain',
  async (url: string) => {
    const response = await settingsApi.growthTools.deleteWhitelistingDomain({ body: { url } })
    return response.data
  },
)

export const setConversionsApiByChannel = createAction(
  'settings/setConversionsApiByChannel',
  (channel: ConversionsApiChannel, conversionApi: Partial<ConversionsApiValue> | null) => ({
    payload: {
      channel,
      value: conversionApi,
    },
  }),
)

export const setGreeting = createAsyncAction(
  'settings/setGreeting',
  async (greetingText: string) => {
    const response = await settingsApi.facebook.setGreeting({
      body: { greeting_text: greetingText },
    })

    return response.data
  },
  {
    autoHandleByGuards: [settingsApi.facebook.setGreeting.isGreetingTextError],
  },
)

export const disableUserInput = createAsyncAction(
  'settings/disableUserInput',
  async (isDisabled: boolean) => {
    const response = await settingsApi.facebook.disableUserInput({
      body: { composer_input_disable: isDisabled },
    })

    return response.data
  },
)

export const switchFacebookChannel = createAsyncAction(
  'settings/switchFacebookChannel',
  async ({ status, returnPath = null }: { status: boolean; returnPath?: string | null }) => {
    const response = await settingsApi.facebook.switchFacebookChannel({
      body: { value: status, return_path: returnPath },
    })

    if (response?.data?.redirect_url) {
      window.location.replace(response.data.redirect_url)
    } else {
      window.location.reload()
    }
  },
  {
    onBusinessError(error) {
      alert(error.message, 'danger')
    },
    errorMarkupOptions: {
      scope: 'facebook',
      section: 'settings',
    },
  },
)

export const connectStripe = createAsyncAction<string, void>('settings/connectStripe', async () => {
  const response = await settingsApi.payments.connectStripe()

  return response.data.redirect
})

appListenerMiddleware.startListening({
  matcher: connectStripe.fulfilled.match,
  effect: (action) => {
    window.location.href = action.payload
  },
})

export const disconnectStripe = createAsyncAction('settings/disconnectStripe', async () => {
  const response = await settingsApi.payments.disconnectStripe()

  return response.data
})

appListenerMiddleware.startListening({
  matcher: disconnectStripe.fulfilled.match,
  effect: () => {
    window.location.reload()
  },
})

export const setCurrency = createAsyncAction('settings/setCurrency', async (currency: string) => {
  const response = await settingsApi.payments.setCurrency({ body: { currency } })

  return response.data
})

export const setPaymentNotifySettings = createAsyncAction(
  'settings/setPaymentNotifySettings',
  async (types: string[]) => {
    const response = await settingsApi.payments.setPaymentNotifySettings({ body: { types } })

    return response.data
  },
)

export const setStripeChargeEmailNotification = createAsyncAction(
  'settings/setStripeChargeEmailNotification',
  async (notify: boolean) => {
    const response = await settingsApi.payments.setStripeChargeEmailNotification({
      body: { notify },
    })

    return response.data
  },
)

export const getHandoverReceivers = createAsyncAction(
  'settings/getHandoverReceivers',
  async () => {
    const response = await settingsApi.handover.getHandoverReceivers()

    return response.data
  },
  {
    onError: (error, _, { dispatch }) => {
      if (isRequestError(error)) {
        dispatch(addNotification(error?.response?.error, 'danger'))
      }
    },
  },
)

export const setLiveChatReceiver = createAsyncAction(
  'settings/setLiveChatReceiver',
  async (app_id: string) => {
    const response = await settingsApi.handover.setLiveChatReceiver({ body: { app_id } })

    return response.data
  },
)

export const updateSettingsState = createAction<{ settings: SettingsType }>(
  'settings/updateSettingsState',
)

export const setUrlShortener = createAsyncAction(
  'settings/setUrlShortener',
  async (service: string, { dispatch }) => {
    const {
      data: { settings },
    } = await settingsApi.general.setUrlShortener({ body: { service } })

    if (settings !== undefined) {
      dispatch(updateSettingsState({ settings }))
    }
  },
)

export const setBodyUrlShortener = createAsyncAction(
  'settings/setBodyUrlShortener',
  async (service: string, { dispatch }) => {
    const { data } = await settingsApi.general.setBodyUrlShortener({ body: { service } })

    dispatch(updateSettingsState(data))
    return data
  },
)

export const setWhatsAppUrlShortener = createAsyncAction(
  'settings/setWhatsAppUrlShortener',
  async (service: string, { dispatch }) => {
    const { data } = await settingsApi.whatsapp.setWhatsAppUrlShortener({ body: { service } })

    dispatch(updateSettingsState(data))
    return data
  },
)

export const setGate = createAsyncAction(
  'settings/setGate',
  async (gate: string | undefined, { dispatch }) => {
    const { data } = await settingsApi.liveChat.setGate({ body: { gate: gate ?? 'open' } })

    alert(l.translate('Settings have been saved!'), 'success')

    dispatch(updateSettingsState(data))
    return data
  },
)

export const changeLcThreadsFilter = createAsyncAction(
  'settings/changeLcThreadsFilter',
  async (enabled: boolean | undefined, { dispatch }) => {
    const { data } = await settingsApi.liveChat.changeLcThreadsFilter({
      body: { enabled: enabled ?? false },
    })

    alert(l.translate('Settings have been saved!'), 'success')

    dispatch(updateSettingsState(data))
    return data
  },
)

export const setNotificationEmail = createAsyncAction(
  'settings/setNotificationEmail',
  async (email: string, { dispatch }) => {
    const { data } = await settingsApi.notifications.setNotificationEmail({
      body: { email },
    })

    dispatch(updateSettingsState(data))
    return data
  },
  {
    autoHandleByGuards: [settingsApi.notifications.setNotificationEmail.isInvalidEmailError],
  },
)

export const setNotificationPhone = createAsyncAction(
  'settings/setNotificationPhone',
  async (payload: { phone: string; isCheckAcceptLegal?: boolean }, { dispatch }) => {
    const { data } = await settingsApi.notifications.setNotificationPhone({
      body: payload,
    })

    dispatch(updateSettingsState(data))
    return data
  },
  {
    autoHandleByGuards: [settingsApi.notifications.setNotificationPhone.isInvalidPhoneError],
  },
)

export const resendConfirmationNotificationEmail = createAsyncAction(
  'settings/resendConfirmationNotificationEmail',
  async (_, { dispatch }) => {
    const { data } = await settingsApi.notifications.resendConfirmationNotificationEmail()

    dispatch(updateSettingsState(data))
    return data
  },
)

export const resetUnverifiedNotificationEmail = createAsyncAction(
  'settings/resetUnverifiedNotificationEmai',
  async (_, { dispatch }) => {
    const { data } = await settingsApi.notifications.resetUnverifiedNotificationEmail()

    dispatch(updateSettingsState(data))

    return data
  },
)

export const setPageName = createAsyncAction('settings/setPageName', async (page_name: string) => {
  await settingsApi.general.setPageName({ body: { page_name } })
  alert(l.translate('Account name changed!'), 'success')
})

export const setNotifyAdmin = createAsyncAction(
  'settings/setNotifyAdmin',
  async (types: string[], { dispatch }) => {
    const { data } = await settingsApi.notifications.setNotifyAdmin({
      body: { types },
    })

    alert(l.translate('Settings have been saved!'), 'success')

    dispatch(updateSettingsState(data))
    return data
  },
)

export const setLivechatSettings = createAsyncAction(
  'settings/setLivechatSettings',
  async (payload: { settings_key: string; value: boolean }) => {
    const { data } = await settingsApi.liveChat.setLivechatSettings({ body: payload })

    return data.livechat_settings
  },
)

export const updateLivechatAccountSettings = createAsyncAction(
  'settings/updateLivechatAccountSettings',
  async (payload: LiveChatAccountSettingsRequest) => {
    const { data } = await settingsApi.liveChat.updateLivechatAccountSettings({ body: payload })

    if (payload.settings_key !== LiveChatAccountSettings.AUTO_ASSIGN_TYPE) {
      alert(l.translate('Settings have been saved!'), 'success')
    } else if (payload.value) {
      alert(l.translate('Auto-Assignment is on.'), 'success')
    } else {
      alert(l.translate('Auto-Assignment is off.'), 'success')
    }

    return data
  },
)

export const getListTimezones = createAsyncAction('settings/getListTimezones', async () => {
  const { data } = await settingsApi.general.getListTimezones()

  return data.timezones
})

export const setBotTimezone = createAsyncAction(
  'settings/setBotTimezone',
  async ({ timezone, isSilent = false }: { timezone: string; isSilent?: boolean }) => {
    await settingsApi.general.setTimezone({ body: { timezone } })

    if (!isSilent) {
      alert(l.translate('Account Time Zone changed!'), 'success')
    }
  },
  {
    autoHandleByGuards: [settingsApi.general.setTimezone.isInvalidTimezoneError],
  },
)

export const setMobileNotifyAdminPushEnabled = createAsyncAction(
  'settings/setMobileNotifyAdminPushEnabled',
  async (value: boolean) => {
    const { data } = await settingsApi.notifications.setMobileNotifyAdminPushEnabled({
      body: { mobile_notify_admin_push_enabled: value },
    })

    alert(l.translate('Settings have been saved!'), 'success')
    return data.mobile_notify_admin_push_enabled
  },
)

export const setSkipQuickCampaignsModal = createAsyncAction(
  'settings/setSkipQuickCampaignsModal',
  (enabled: boolean) =>
    settingsApi.display.setSkipQuickCampaignsModal({
      body: { skip_flow_templates_modal: enabled },
    }),
)

export const setAudienceDeletedSubscribersVisibility = createAsyncAction(
  'settings/setAudienceDeletedSubscribersVisibility',
  async (value: boolean, { dispatch }) => {
    await settingsApi.display.setAudienceOnlyActive({
      body: { value },
    })

    alert(l.translate('Settings have been saved!'), 'success')
    dispatch(audienceActions.dropSubscribersList())
  },
)

export const getMessagingFeatureReview = createAsyncAction(
  'settings/getMessagingFeatureReview',
  async () => {
    const { data } = await settingsApi.general.getMessagingFeatureReview()

    return data.features
  },
)

export const fetchPricingLimitInfo = createAsyncAction(
  'settings/fetchPricingLimitInfo',
  async (_, { getState }) => {
    const shouldFetch = isFetchPricingLimitInfo(getState())
    if (!shouldFetch) return

    const { data } = await settingsApi.payments.fetchPricingLimitInfo()

    return data
  },
)

export const ensureSettings = createAsyncAction(
  'settings/ensureSettings',
  async (_, { dispatch, getState }) => {
    const state = getState()
    const isStateSettingsEmpty = !state.settings.item

    // We initialize display preferences from initialData when the page loads for UI.
    const isStateSettingsOnlyWithDisplayPreferences =
      state.settings.item?.display_preferences && size(state.settings.item) === 1

    if (isStateSettingsEmpty || isStateSettingsOnlyWithDisplayPreferences) {
      await dispatch(fetchSettings()).unwrap()
    }
  },
)

export const handoverUpdatedNotification = createAction(
  'settings/handoverUpdatedNotification',
  (data: Record<string, unknown>) => ({
    payload: data,
  }),
)

export const updateLiveChatFilterSetting = createAction(
  'settings/updateLiveChatFilter',
  (value: string | number | boolean) => ({
    payload: value,
  }),
)

export const setDisplayPreference = createAction(
  'settings/setDisplayPreference',
  (preferenceName: string, value: string | number | boolean) => ({
    payload: {
      preference: preferenceName,
      value,
    },
  }),
)

export const setDisplayPreferences = createAction(
  'settings/setDisplayPreferences',
  (displayPreferences: Record<string, unknown>) => ({
    payload: displayPreferences,
  }),
)

export const setInitialSettings = createAction(
  'settings/setInitialSettings',
  (settings: ResponseData) => ({
    payload: settings,
  }),
)

interface CapiSettingsUpdateData {
  capi_integration_status: ConversionsApiStatus | null
  channel: ConversionsApiChannel
  is_automated_event_sending_enabled: boolean | null
  dataset_id: number | null
}

anotherTabNotificationsListener.on(
  WebSocketEvents.CAPI_SETTINGS_UPDATED,
  (data: CapiSettingsUpdateData, dispatch) => {
    const {
      channel,
      capi_integration_status: status,
      is_automated_event_sending_enabled,
      dataset_id,
    } = data

    const isDisconnected =
      status === null || is_automated_event_sending_enabled === null || dataset_id === null
    const value: ConversionsApiValue | null = isDisconnected
      ? null
      : {
          status,
          is_automated_event_sending_enabled,
          dataset_id,
        }

    dispatch(setConversionsApiByChannel(channel, value))
  },
)

accountNotificationsListener.on('account_handover_updated', (data, dispatch) => {
  dispatch(handoverUpdatedNotification(data))

  if (isPathnameInclude('/settings')) {
    dispatch(getHandoverReceivers())
  }
})

accountNotificationsListener.on('lc_threads_filter_changed', (data, dispatch) => {
  dispatch(updateLiveChatFilterSetting(data.model.value))
})

anotherTabNotificationsListener.on('display_preferences_updated', (data, dispatch) => {
  dispatch(setDisplayPreferences(data.display_preferences))
})
